import { useState, useRef, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Upload, FileText, CheckCircle2, XCircle, AlertCircle, Loader2,
  Search, Zap, Ban, Undo2, Plus, TrendingUp, TrendingDown,
  Calendar, Receipt, Banknote, Sparkles, Clock, Link2, X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

interface ContaBancaria {
  id: number; nome: string; saldo_atual: number
}

interface ExtratoResumo {
  id: number; conta_id: number; conta_nome: string
  nome_arquivo: string; tipo: string; total_itens: number
  criado_em: string
  conciliados: number; pendentes: number; ignorados: number
}

interface SugestaoMatch {
  lancamento_id: number; data: string; valor: number
  tipo: string; descricao: string; score: number
}

interface ExtratoItem {
  id: number; data: string; tipo: 'credit' | 'debit'
  valor: number; descricao: string; documento: string | null
  fit_id: string | null; status: 'pendente' | 'conciliado' | 'ignorado'
  lancamento_id: number | null
  lancamento_atual: { id: number; valor: number; descricao: string; data: string } | null
  sugestoes: SugestaoMatch[]
}

export default function ConciliacaoBancaria() {
  const qc = useQueryClient()
  const inputFile = useRef<HTMLInputElement>(null)
  const [contaId, setContaId] = useState<number | ''>('')
  const [extratoSelId, setExtratoSelId] = useState<number | null>(null)
  const [tipoArquivo, setTipoArquivo] = useState<'ofx' | 'csv'>('ofx')
  const [uploading, setUploading] = useState(false)

  const { data: contas = [] } = useQuery<ContaBancaria[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  const { data: extratos = [], refetch: refetchExtratos } = useQuery<ExtratoResumo[]>({
    queryKey: ['extratos', contaId],
    queryFn: async () => {
      const params = contaId ? { conta_id: contaId } : {}
      return (await api.get('/conciliacao/extratos', { params })).data
    },
  })

  const { data: detalhe, refetch: refetchDet } = useQuery<any>({
    queryKey: ['extrato-detalhe', extratoSelId],
    queryFn: async () => (await api.get(`/conciliacao/extrato/${extratoSelId}`)).data,
    enabled: !!extratoSelId,
  })

  async function upload(f: File) {
    if (!contaId) { toast.error('Selecione a conta bancária primeiro'); return }
    setUploading(true)
    const fd = new FormData()
    fd.append('conta_id', String(contaId))
    fd.append('tipo', tipoArquivo)
    fd.append('arquivo', f)
    try {
      const r = await api.post('/conciliacao/importar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      toast.success(r.data.mensagem || `${r.data.inseridos} itens importados`)
      await refetchExtratos()
      setExtratoSelId(r.data.extrato_id)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Falha ao importar')
    } finally {
      setUploading(false)
    }
  }

  const casar = useMutation({
    mutationFn: async ({ item_id, lancamento_id }: { item_id: number; lancamento_id: number }) =>
      api.post('/conciliacao/casar', { item_id, lancamento_id }),
    onSuccess: () => { toast.success('Conciliado'); refetchDet(); refetchExtratos() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const criarLanc = useMutation({
    mutationFn: async (item_id: number) =>
      api.post('/conciliacao/criar-lancamento', { item_id }),
    onSuccess: () => { toast.success('Lançamento criado e conciliado'); refetchDet(); refetchExtratos() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const ignorar = useMutation({
    mutationFn: async (item_id: number) =>
      api.post('/conciliacao/ignorar', { item_id }),
    onSuccess: () => { toast.success('Item ignorado'); refetchDet(); refetchExtratos() },
  })

  const desfazer = useMutation({
    mutationFn: async (item_id: number) =>
      api.post('/conciliacao/desfazer', { item_id }),
    onSuccess: () => { toast.success('Desfeito'); refetchDet(); refetchExtratos() },
  })

  const autoConciliar = useMutation({
    mutationFn: async () => api.post(`/conciliacao/auto-conciliar/${extratoSelId}`),
    onSuccess: (r: any) => {
      toast.success(`${r.data.casados} item(ns) conciliado(s) automaticamente`)
      refetchDet(); refetchExtratos()
    },
  })

  const itens: ExtratoItem[] = detalhe?.itens || []
  const resumo = detalhe?.resumo || { total: 0, conciliados: 0, pendentes: 0, ignorados: 0 }

  return (
    <div className="p-4 lg:p-6 max-w-[1500px] mx-auto">
      {/* Cabecalho */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2">
          <Link2 size={22} /> Conciliação Bancária
        </h1>
        <p className="text-sm text-ink-500">
          Importe o extrato (OFX/CSV) e case com os lançamentos do sistema para manter o caixa conferido.
        </p>
      </div>

      {/* Painel de upload */}
      <div className="card p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-ink-600">Conta bancária</label>
            <select
              value={contaId}
              onChange={e => setContaId(Number(e.target.value) || '')}
              className="input mt-0.5">
              <option value="">Selecione a conta...</option>
              {contas.map(c => (
                <option key={c.id} value={c.id}>{c.nome} — Saldo {formatMoney(c.saldo_atual)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-ink-600">Tipo de arquivo</label>
            <select value={tipoArquivo} onChange={e => setTipoArquivo(e.target.value as 'ofx' | 'csv')}
              className="input mt-0.5 w-24">
              <option value="ofx">OFX</option>
              <option value="csv">CSV</option>
            </select>
          </div>

          <input
            ref={inputFile}
            type="file"
            accept={tipoArquivo === 'ofx' ? '.ofx' : '.csv,.txt'}
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0]
              if (f) upload(f)
              e.currentTarget.value = ''
            }}
          />
          <button
            disabled={!contaId || uploading}
            onClick={() => inputFile.current?.click()}
            className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
              contaId && !uploading
                ? 'bg-brand-500 hover:bg-brand-600 text-white'
                : 'bg-ink-200 text-ink-400 cursor-not-allowed'
            }`}>
            {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            Importar extrato
          </button>
        </div>
        <p className="text-[11px] text-ink-500 mt-2">
          Dica: todos os bancos (Itaú, BB, Bradesco, Caixa, Santander, Inter, Sicoob, Nubank) exportam OFX no internet banking.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* SIDEBAR - Lista de extratos */}
        <div className="col-span-12 md:col-span-3">
          <div className="card p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">Extratos importados</h3>
              <span className="text-xs text-ink-500">{extratos.length}</span>
            </div>
            {extratos.length === 0 ? (
              <div className="text-center text-xs text-ink-400 py-8">
                <FileText size={24} className="mx-auto mb-2 opacity-40" />
                Nenhum extrato ainda
              </div>
            ) : (
              <div className="space-y-1 max-h-[600px] overflow-y-auto">
                {extratos.map(ext => {
                  const pct = ext.total_itens > 0 ? Math.round((ext.conciliados / ext.total_itens) * 100) : 0
                  return (
                    <button
                      key={ext.id}
                      onClick={() => setExtratoSelId(ext.id)}
                      className={`w-full text-left px-2 py-2 rounded text-xs border transition-all ${
                        extratoSelId === ext.id
                          ? 'bg-brand-50 border-brand-300'
                          : 'bg-white border-ink-200 hover:border-ink-300'
                      }`}>
                      <p className="font-semibold truncate text-ink-900">{ext.nome_arquivo}</p>
                      <p className="text-[10px] text-ink-500">{ext.conta_nome} · {new Date(ext.criado_em).toLocaleDateString('pt-BR')}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <div className="flex-1 h-1 bg-ink-100 rounded">
                          <div className={`h-1 rounded ${pct === 100 ? 'bg-emerald-500' : 'bg-brand-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] font-mono text-ink-600">{pct}%</span>
                      </div>
                      <p className="text-[10px] text-ink-500 mt-0.5">
                        {ext.conciliados}/{ext.total_itens} conciliados
                      </p>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* CORPO - Conciliacao */}
        <div className="col-span-12 md:col-span-9">
          {!extratoSelId ? (
            <div className="card p-10 text-center text-ink-400">
              <Upload size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">Importe um extrato para começar a conciliar.</p>
              <p className="text-xs mt-1">Selecione a conta → clique em "Importar extrato"</p>
            </div>
          ) : (
            <>
              {/* Resumo + acoes */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="card p-3">
                  <p className="text-[10px] text-ink-500 uppercase tracking-wider">Total</p>
                  <p className="text-xl font-bold text-ink-900">{resumo.total}</p>
                </div>
                <div className="card p-3 bg-emerald-50 border-emerald-200">
                  <p className="text-[10px] text-emerald-700 uppercase tracking-wider">Conciliados</p>
                  <p className="text-xl font-bold text-emerald-700">{resumo.conciliados}</p>
                </div>
                <div className="card p-3 bg-amber-50 border-amber-200">
                  <p className="text-[10px] text-amber-700 uppercase tracking-wider">Pendentes</p>
                  <p className="text-xl font-bold text-amber-700">{resumo.pendentes}</p>
                </div>
                <div className="card p-3 bg-ink-50 border-ink-200">
                  <p className="text-[10px] text-ink-600 uppercase tracking-wider">Ignorados</p>
                  <p className="text-xl font-bold text-ink-700">{resumo.ignorados}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm">Linhas do extrato</h3>
                <button
                  onClick={() => autoConciliar.mutate()}
                  disabled={autoConciliar.isPending || resumo.pendentes === 0}
                  className="px-3 py-1.5 rounded text-xs bg-gradient-to-r from-purple-500 to-purple-700 text-white font-semibold flex items-center gap-1 disabled:opacity-50 hover:shadow-md">
                  {autoConciliar.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  Conciliar automaticamente (score 100%)
                </button>
              </div>

              {/* Lista de itens */}
              <div className="space-y-1.5">
                {itens.map(item => {
                  const isCred = item.tipo === 'credit'
                  const statusCor = item.status === 'conciliado' ? 'bg-emerald-50 border-emerald-300'
                    : item.status === 'ignorado' ? 'bg-ink-50 border-ink-200 opacity-60'
                    : 'bg-white border-ink-200'
                  return (
                    <div key={item.id} className={`card p-3 border-l-4 ${statusCor} ${isCred ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
                      <div className="flex items-start gap-3">
                        {/* Coluna 1: dados do extrato */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            {isCred ? <TrendingUp size={14} className="text-emerald-600" />
                                    : <TrendingDown size={14} className="text-red-600" />}
                            <p className="font-semibold text-sm text-ink-900">{item.descricao}</p>
                            {item.status === 'conciliado' && <CheckCircle2 size={14} className="text-emerald-600" />}
                            {item.status === 'ignorado' && <Ban size={14} className="text-ink-400" />}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-ink-500">
                            <span className="flex items-center gap-1"><Calendar size={10} />{new Date(item.data + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
                            {item.documento && <span className="font-mono">{item.documento}</span>}
                            {item.fit_id && <span className="font-mono text-[10px]">{item.fit_id}</span>}
                          </div>
                          {/* Status: conciliado */}
                          {item.status === 'conciliado' && item.lancamento_atual && (
                            <div className="mt-2 p-2 bg-emerald-100/50 rounded text-xs">
                              <span className="text-emerald-800 font-semibold">Casado com:</span>{' '}
                              <span>{item.lancamento_atual.descricao}</span>
                              {' '}<span className="text-ink-500">({formatMoney(item.lancamento_atual.valor)})</span>
                            </div>
                          )}
                          {/* Sugestoes */}
                          {item.status === 'pendente' && item.sugestoes.length > 0 && (
                            <div className="mt-2 space-y-1">
                              <p className="text-[10px] text-ink-500 uppercase tracking-wider flex items-center gap-1">
                                <Sparkles size={10} className="text-purple-500" /> Sugestões
                              </p>
                              {item.sugestoes.map(s => (
                                <button
                                  key={s.lancamento_id}
                                  onClick={() => casar.mutate({ item_id: item.id, lancamento_id: s.lancamento_id })}
                                  className={`w-full text-left px-2 py-1.5 rounded text-xs border transition-all flex items-center gap-2 ${
                                    s.score === 100
                                      ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
                                      : s.score >= 80
                                        ? 'bg-amber-50 border-amber-300 hover:bg-amber-100'
                                        : 'bg-ink-50 border-ink-200 hover:bg-ink-100'
                                  }`}>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                    s.score === 100 ? 'bg-emerald-600 text-white'
                                    : s.score >= 80 ? 'bg-amber-500 text-white'
                                    : 'bg-ink-400 text-white'
                                  }`}>{s.score}%</span>
                                  <span className="flex-1 truncate">{s.descricao}</span>
                                  <span className="font-mono text-ink-600">{formatMoney(s.valor)}</span>
                                  <CheckCircle2 size={12} className="text-emerald-600" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Coluna 2: valor */}
                        <div className="text-right flex-shrink-0">
                          <p className={`text-lg font-bold ${isCred ? 'text-emerald-700' : 'text-red-700'}`}>
                            {isCred ? '+' : '−'}{formatMoney(item.valor)}
                          </p>
                          {/* Acoes */}
                          <div className="flex items-center gap-1 mt-1 justify-end">
                            {item.status === 'pendente' && (
                              <>
                                <button
                                  onClick={() => {
                                    if (confirm(`Criar lançamento a partir dessa linha?\n\n${item.descricao}\n${formatMoney(item.valor)}`)) {
                                      criarLanc.mutate(item.id)
                                    }
                                  }}
                                  className="p-1.5 rounded bg-brand-100 hover:bg-brand-200 text-brand-700"
                                  title="Criar lançamento">
                                  <Plus size={12} />
                                </button>
                                <button
                                  onClick={() => ignorar.mutate(item.id)}
                                  className="p-1.5 rounded bg-ink-100 hover:bg-ink-200 text-ink-600"
                                  title="Ignorar">
                                  <Ban size={12} />
                                </button>
                              </>
                            )}
                            {(item.status === 'conciliado' || item.status === 'ignorado') && (
                              <button
                                onClick={() => desfazer.mutate(item.id)}
                                className="p-1.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-700"
                                title="Desfazer">
                                <Undo2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
